/**
 * AgentEditReviewModal Component
 *
 * Modal for reviewing pending CLAUDE.md edits submitted by Lead Agents.
 * Shows a diff view (side-by-side or unified) and allows the user to
 * approve or reject the proposed change.
 *
 * @module components/claude-templates/AgentEditReviewModal
 */

import { useState, useCallback, useMemo, memo } from 'react';
import {
  X,
  Check,
  XCircle,
  Loader2,
  FileText,
  Bot,
  ChevronLeft,
  ChevronRight,
  Clock,
  MessageSquare,
  Pencil,
} from 'lucide-react';
import { SideBySideDiff, UnifiedDiff, parseDiff } from './PushPreviewModal';

const API_BASE = '/api';

/**
 * Approve a pending edit
 */
async function approvePendingEdit(editId) {
  const res = await fetch(`${API_BASE}/claude-templates/pending-edits/${editId}/approve`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to approve edit');
  }
  return res.json();
}

/**
 * Reject a pending edit
 */
async function rejectPendingEdit(editId, reason) {
  const res = await fetch(`${API_BASE}/claude-templates/pending-edits/${editId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ reason: reason || undefined }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to reject edit');
  }
  return res.json();
}

/**
 * Generate a unified diff from old and new content strings
 */
function generateClientDiff(oldContent, newContent) {
  const oldLines = (oldContent || '').split('\n');
  const newLines = (newContent || '').split('\n');

  const diffLines = [];
  const maxLen = Math.max(oldLines.length, newLines.length);

  // Simple line-by-line comparison (server provides proper unified diff when available)
  let i = 0, j = 0;
  while (i < oldLines.length || j < newLines.length) {
    if (i < oldLines.length && j < newLines.length && oldLines[i] === newLines[j]) {
      diffLines.push(' ' + oldLines[i]);
      i++;
      j++;
    } else if (i < oldLines.length && (j >= newLines.length || oldLines[i] !== newLines[j])) {
      diffLines.push('-' + oldLines[i]);
      i++;
    } else {
      diffLines.push('+' + newLines[j]);
      j++;
    }
  }

  return diffLines.join('\n');
}

/**
 * AgentEditReviewModal
 *
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Array} props.pendingEdits - Array of pending edit objects
 * @param {number} props.currentIndex - Index of currently displayed edit
 * @param {function} props.onNavigate - Callback to navigate between edits (index)
 * @param {function} props.onApprove - Callback after successful approve
 * @param {function} props.onReject - Callback after successful reject
 * @param {function} props.onClose - Close modal callback
 */
const AgentEditReviewModal = memo(function AgentEditReviewModal({
  isOpen,
  pendingEdits = [],
  currentIndex = 0,
  onNavigate,
  onApprove,
  onReject,
  onClose,
}) {
  const [viewMode, setViewMode] = useState('unified');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const edit = pendingEdits[currentIndex] || null;
  const totalEdits = pendingEdits.length;

  // Parse diff for display
  const parsedDiff = useMemo(() => {
    if (!edit) return [];
    // Use server-provided diff if available, otherwise generate client-side
    if (edit.diff) {
      return parseDiff(edit.diff);
    }
    if (edit.oldContent != null && edit.newContent != null) {
      return parseDiff(generateClientDiff(edit.oldContent, edit.newContent));
    }
    return [];
  }, [edit]);

  // Calculate change stats
  const stats = useMemo(() => {
    if (!parsedDiff.length) return { added: 0, removed: 0, unchanged: 0 };
    return parsedDiff.reduce((acc, line) => {
      acc[line.type] = (acc[line.type] || 0) + 1;
      return acc;
    }, { added: 0, removed: 0, unchanged: 0 });
  }, [parsedDiff]);

  const handleApprove = useCallback(async () => {
    if (!edit) return;
    setIsProcessing(true);
    setError(null);
    try {
      const result = await approvePendingEdit(edit.id);
      onApprove?.(edit, result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [edit, onApprove]);

  const handleReject = useCallback(async () => {
    if (!edit) return;
    setIsProcessing(true);
    setError(null);
    try {
      const result = await rejectPendingEdit(edit.id, rejectReason);
      setShowRejectInput(false);
      setRejectReason('');
      onReject?.(edit, result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [edit, rejectReason, onReject]);

  const handleRejectClick = useCallback(() => {
    if (showRejectInput) {
      handleReject();
    } else {
      setShowRejectInput(true);
    }
  }, [showRejectInput, handleReject]);

  const handleCancelReject = useCallback(() => {
    setShowRejectInput(false);
    setRejectReason('');
  }, []);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) onNavigate?.(currentIndex - 1);
  }, [currentIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (currentIndex < totalEdits - 1) onNavigate?.(currentIndex + 1);
  }, [currentIndex, totalEdits, onNavigate]);

  if (!isOpen || !edit) return null;

  const submittedAt = edit.submittedAt
    ? new Date(edit.submittedAt).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-6xl mx-4 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Pencil className="w-5 h-5 text-orange-400" />
            <h3 className="text-lg font-semibold text-white">Agent Edit Review</h3>
            {totalEdits > 1 && (
              <span className="px-2 py-0.5 text-xs font-medium text-orange-300 bg-orange-500/20 border border-orange-500/30 rounded-full">
                {currentIndex + 1} von {totalEdits}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info Bar */}
        <div className="px-6 py-3 bg-gray-800/50 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1.5 text-gray-400">
              <Bot className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 font-medium">
                {edit.submittedBy?.displayName || edit.submittedBy?.name || 'Agent'}
              </span>
            </span>
            <span className="text-gray-600">→</span>
            <span className="text-gray-400">
              Template: <span className="text-white font-medium">
                {edit.template?.name || 'Unknown'}
              </span>
            </span>
            <span className="text-gray-600">→</span>
            <span className="flex items-center gap-1.5 text-gray-400">
              <Bot className="w-4 h-4" />
              <span className="text-white font-medium">
                {edit.targetAgent?.displayName || edit.targetAgent?.name || 'Agent'}
              </span>
            </span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <Clock className="w-3.5 h-3.5" />
              {submittedAt}
            </span>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewMode('side-by-side')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                viewMode === 'side-by-side'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Side by Side
            </button>
            <button
              type="button"
              onClick={() => setViewMode('unified')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                viewMode === 'unified'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Unified
            </button>
          </div>
        </div>

        {/* Edit Summary */}
        {edit.editSummary && (
          <div className="px-6 py-2.5 bg-purple-900/10 border-b border-gray-700 flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-purple-200">{edit.editSummary}</p>
          </div>
        )}

        {/* Stats Bar */}
        {parsedDiff.length > 0 && (
          <div className="px-6 py-2 border-b border-gray-700 flex items-center gap-4 text-xs flex-shrink-0">
            <span className="text-green-400">+{stats.added} added</span>
            <span className="text-red-400">-{stats.removed} removed</span>
            <span className="text-gray-500">{stats.unchanged} unchanged</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="px-6 py-2 bg-red-900/20 border-b border-gray-700 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Diff Content */}
        <div className="h-[55vh] overflow-auto">
          {parsedDiff.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-500">No changes to display</p>
              </div>
            </div>
          ) : viewMode === 'side-by-side' ? (
            <SideBySideDiff parsedDiff={parsedDiff} />
          ) : (
            <UnifiedDiff parsedDiff={parsedDiff} />
          )}
        </div>

        {/* Actions Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          {/* Navigation (for multiple pending edits) */}
          <div className="flex items-center gap-2">
            {totalEdits > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={currentIndex === 0 || isProcessing}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-30"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-400">
                  {currentIndex + 1} / {totalEdits}
                </span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={currentIndex === totalEdits - 1 || isProcessing}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-30"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Reject Reason Input + Action Buttons */}
          <div className="flex items-center gap-3">
            {showRejectInput && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reject reason (optional)"
                  className="w-64 px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleReject();
                    if (e.key === 'Escape') handleCancelReject();
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleCancelReject}
                  className="px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={handleRejectClick}
              disabled={isProcessing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-300 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg transition-colors disabled:opacity-50"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Reject
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={isProcessing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors disabled:opacity-50"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AgentEditReviewModal;
